import { create } from "zustand";
import { persist } from "zustand/middleware";
import CONFIGS from "@/core/utils/configs";
import { EnvironmentName } from "@/store/useEnvironmentStore.types";
import type {
  EnvironmentState,
  EnvironmentActions,
} from "@/store/useEnvironmentStore.types";

export { EnvironmentName } from "@/store/useEnvironmentStore.types";

const normalizeBranchName = (branchName: string): string => {
  return branchName
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 63);
};

export const getReviewAppApiUrl = (branchName?: string): string => {
  if (!branchName) return "";
  return `https://${normalizeBranchName(branchName)}.api.reviewapps.riseup.ai`;
};

export const getReviewAppFrontendUrl = (branchName?: string): string => {
  if (!branchName) return "";
  return `https://${normalizeBranchName(branchName)}.web.reviewapps.riseup.ai/`;
};

const initialState: EnvironmentState = {
  env: EnvironmentName.Preproduction,
  reviewAppBranchName: "",
};

export const useEnvironmentStore = create<
  EnvironmentState & EnvironmentActions
>()(
  persist(
    (set, get) => ({
      ...initialState,

      setEnv: (env, branchName) =>
        set({
          env,
          reviewAppBranchName: branchName || "",
        }),

      reset: () => set(initialState),

      getApiUrl: () => {
        const { env, reviewAppBranchName } = get();
        if (env === EnvironmentName.ReviewApp && reviewAppBranchName) {
          return getReviewAppApiUrl(reviewAppBranchName);
        }
        return CONFIGS.API_URL;
      },

      getFrontendUrl: () => {
        if (
          CONFIGS.ENVIRONMENT === "prod" ||
          CONFIGS.ENVIRONMENT === "@env@" ||
          CONFIGS.ENVIRONMENT === "development"
        ) {
          return null;
        }
        const { env, reviewAppBranchName } = get();
        if (env === EnvironmentName.ReviewApp && reviewAppBranchName) {
          return getReviewAppFrontendUrl(reviewAppBranchName);
        }
        if (env === EnvironmentName.Preproduction) {
          return "https://preprod.riseup.ai/";
        }
        return null;
      },
    }),
    { name: "environment-store" },
  ),
);
